import { z } from "zod";
/**
 * Zod schema for LLM completion response
 * Validates OpenAI-compatible responses from adapters
 */
const ToolCallSchema = z.object({
    id: z.string(),
    type: z.literal("function"),
    function: z.object({
        name: z.string(),
        arguments: z.string(),
    }),
});
/**
 * Content block for Claude extended thinking responses.
 * When thinking is enabled, Claude returns an array of content blocks instead of a string.
 */
const ThinkingContentBlockSchema = z.object({
    type: z.literal("thinking"),
    thinking: z.string(),
    signature: z.string().optional(),
});
const TextContentBlockSchema = z.object({
    type: z.literal("text"),
    text: z.string(),
});
const RedactedThinkingContentBlockSchema = z.object({
    type: z.literal("redacted_thinking"),
    data: z.string(),
});
const ContentBlockSchema = z.union([
    ThinkingContentBlockSchema,
    TextContentBlockSchema,
    RedactedThinkingContentBlockSchema,
]);
/**
 * Message content can be:
 * - A string (standard OpenAI format)
 * - An array of content blocks (Claude extended thinking format)
 * - null/undefined
 */
const MessageContentSchema = z.union([
    z.string(),
    z.array(ContentBlockSchema),
]).nullable().optional();
const MessageSchema = z.object({
    role: z.string(),
    content: MessageContentSchema,
    tool_calls: z.array(ToolCallSchema).optional(),
});
const DeltaToolCallSchema = z.object({
    index: z.number(),
    id: z.string().optional(),
    type: z.literal("function").optional(),
    function: z.object({
        name: z.string().optional(),
        arguments: z.string().optional(),
    }).optional(),
});
const DeltaSchema = z.object({
    role: z.string().optional(),
    content: z.string().nullable().optional(),
    tool_calls: z.array(DeltaToolCallSchema).optional(),
});
const ChoiceSchema = z.object({
    index: z.number(),
    message: MessageSchema.optional(),
    delta: DeltaSchema.optional(),
    finish_reason: z.string().nullable().optional(),
});
const UsageSchema = z.object({
    prompt_tokens: z.number(),
    completion_tokens: z.number(),
    total_tokens: z.number(),
    // Anthropic extended thinking token counts (optional)
    thinking_tokens: z.number().optional(),
    cache_creation_input_tokens: z.number().optional(),
    cache_read_input_tokens: z.number().optional(),
});
export const LLMCompletionResponseSchema = z.object({
    id: z.string(),
    object: z.string(),
    created: z.number(),
    model: z.string(),
    choices: z.array(ChoiceSchema),
    usage: UsageSchema.optional(),
});
/**
 * Helper to extract text content from a message that might have content blocks.
 * Returns the concatenated text from all text blocks, or the string content directly.
 */
export function extractTextContent(content) {
    if (!content)
        return '';
    if (typeof content === 'string')
        return content;
    return content
        .filter((block) => block.type === 'text')
        .map(block => block.text)
        .join('');
}
/**
 * Helper to extract thinking content from a message with content blocks.
 * Returns the concatenated thinking from all thinking blocks.
 */
export function extractThinkingContent(content) {
    if (!content || typeof content === 'string')
        return '';
    return content
        .filter((block) => block.type === 'thinking')
        .map(block => block.thinking)
        .join('\n\n');
}
/**
 * Helper to validate LLM responses
 */
export function validateLLMResponse(response) {
    const result = LLMCompletionResponseSchema.safeParse(response);
    if (result.success) {
        return { success: true, data: result.data };
    }
    const errorMessages = result.error.issues.map(err => {
        const path = err.path.length > 0 ? err.path.join('.') : 'root';
        return `${path}: ${err.message}`;
    });
    return {
        success: false,
        error: `Invalid LLM response:\n${errorMessages.join('\n')}`
    };
}
/**
 * Abstract base class for LLM adapters
 * Uses ToolBase and ToolDescription directly - no redefinition needed!
 */
export class BaseLLMAdapter {
    /**
     * Convert ToolBase[] to OpenAI tool format
     * ToolDescription already IS the OpenAI format, so we just extract it!
     * Returns plain objects ready for JSON serialization
     */
    toolsToOpenAIFormat(tools) {
        // ToolDescription already matches OpenAI format exactly
        // Extract the properties to plain objects for API calls
        return tools.map(tool => ({
            type: tool.description.type,
            function: {
                name: tool.description.function.name,
                description: tool.description.function.description,
                parameters: tool.description.function.parameters,
            }
        }));
    }
    /**
     * Process messages to ensure OpenAI compatibility
     */
    processMessages(messages, system) {
        const processed = [];
        if (system) {
            processed.push({
                role: "system",
                content: system
            });
        }
        let messageList = messages;
        if (messages.length === 1 && Array.isArray(messages[0])) {
            messageList = messages[0];
        }
        for (const msg of messageList) {
            if (!msg || typeof msg !== "object") {
                continue;
            }
            const role = msg.role || "user";
            const content = msg.content || "";
            if (role === "system" && system) {
                continue;
            }
            const processedMsg = {
                role: role,
                content: content,
            };
            if (msg.name)
                processedMsg.name = msg.name;
            if (msg.tool_calls)
                processedMsg.tool_calls = msg.tool_calls;
            if (msg.tool_call_id)
                processedMsg.tool_call_id = msg.tool_call_id;
            processed.push(processedMsg);
        }
        return processed;
    }
}
